import React, { useState, useEffect, useCallback } from 'react';
import { FieldError } from 'react-hook-form';

import * as subjectApi from '@api/subject';

import { useAppSelector, useAppDispatch } from '@store/hook';
import { selectUser } from '@store/user';
import { open as modalOpen } from '@store/modal';

import { ROLE } from '@common';
import { SearchDto, SubjectDto } from '@dto';


interface SubjectSelectProp {
  readonly value?: number | undefined;
  readonly onChange: (
    id: number,
    subject: SubjectDto

  ) => void;
  readonly courseId?: number;
  /**
   * 커리큘럼 타입
   */

  readonly disabled?: boolean;
  readonly hideLabel?: boolean;
  readonly className?: string;
  readonly isTeacher?: boolean;
  readonly isParent?: boolean;
  readonly childId?: number;
  readonly error?: FieldError | undefined;
  readonly selectClassName?: string;
  readonly defaultValue?: number;
}

export const SubjectSelect: React.FC<SubjectSelectProp> = ({
  value: _value = -1,
  courseId,
  onChange,

  disabled: _disabled = false,
  hideLabel = false,
  className = '',
  isTeacher = false,
  isParent = false,
  childId: _childId,
  error: _error,
  selectClassName,
  defaultValue,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<SubjectDto[]>([]);
  const [error, setError] = useState<FieldError | undefined>();
  const [value, setValue] = useState<number | undefined>(_value);
  const [disabled, setDisabled] = useState<boolean>(_disabled);
  const [childId, setChildId] = useState<number | undefined>();

  const { user } = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // 사용자 정보가 필요하기 때문에 STORE에 주입이 되었는지 확인 후
      // 동작하도록 한다.
      if (!user) {
        return;
      }

      // requiredClassId가 true이면서 classId가 없을 경우
      // 더 이상 처리 안되도록 한다.
      if (!courseId) {
        setData([])
        return;
      }

      const search: SearchDto = {
        page: 1,
        limit: 100,
        search: {

        },
      };

      // 선생님일 경우 search에 user id를 넣어준다
      const orgs = user.orgs?.length || 1
      if (isTeacher && user.role && user.role.id === ROLE.MEM_TEACHER && orgs < 2) {
        search.search.user = {
          id: user.id as number,
        };
      }

      if (courseId) {
        search.search.course = {
          id: courseId,
        };
      }

      const { data } = await subjectApi.list(search);
      setData([...data]);

      if (defaultValue) {
        if (onChange) {
          const subject: SubjectDto = [...data].find(
            (c: SubjectDto) => c.id === defaultValue,
          ) as SubjectDto;
          onChange(defaultValue, subject);
        }
      }
    } catch (error: any) {
      dispatch(
        modalOpen({
          type: 'alert',
          isOpen: true,
          title: '오류',
          message: error.message
            ? error.message
            : `오류가 발생하였습니다. [오류코드 ${error.status}]`,
        }),
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [, courseId, user, isTeacher, isParent, childId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (_disabled !== disabled) {
      setDisabled(_disabled);
    }
  }, [_disabled, disabled]);

  useEffect(() => {
    setValue(_value);
  }, [_value]);

  useEffect(() => {
    setError(_error);
  }, [_error, setError]);

  useEffect(() => {
    setChildId(_childId);
  }, [_childId]);

  const handleChange = (e: any) => {
    const value: number = Number(e.target.value);

    if (!isNaN(value)) {
      setValue(value);
      if (onChange) {
        const subject: SubjectDto = [...data].find((c: SubjectDto) => c.id === value) as SubjectDto;
        onChange(value, subject);
      }
    }
  };

  /**
   * type에 따라 표현되는 라벨이 달라진다
   * @returns
   */
  const label = (): string => {
    return '주제';
  };

  return (
    <div className={`form-control w-full ${className}`}>
      {!hideLabel && (
        <label className="label">
          <span className="label-text">{label()}</span>
        </label>
      )}
      <select
        aria-label='select item'
        className={`select select-bordered ${loading && 'loading'} text-sm font-normal w-full ${selectClassName ? selectClassName : ''
          }`}
        disabled={disabled}
        value={value}
        onChange={handleChange}>
        <option value={-1}  >
          주제를 선택해주세요
        </option>
        {data.map((value, index) => (
          <option value={value.id} key={index}>
            <>{value.title}</>
          </option>
        ))}
      </select>
      <span className="mt-1 text-xs text-error">{error?.message}</span>
    </div>
  );
};
